import { useEffect, useRef, useState } from 'react';

/**
 * Jumpscare overlay — full-screen black bg + centered image/video + audio.
 * Mirrors the HypeVison desktop app (apps/hype-vison) so streamers who
 * don't want to install the companion app can still play jumpscares via
 * an OBS browser source.
 *
 * Server flow:
 *   /api/abilities/:id/redeem (or card redeem) → eventbus.publishJumpscare()
 *     → Redis PUBLISH streamer:<id>:jumpscare
 *     → ws-gateway forwards as socket 'jumpscare' event
 *     → parent (OverlayApp / WidgetApp) feeds it here via `event` prop
 *
 * Sequential play: if two jumpscares land within a few seconds, the
 * second waits in the queue rather than stacking on top of the first.
 * Matches HypeVison's pump semantics in apps/hype-vison/src/lib/stores/jumpscare.ts.
 */

export interface JumpscarePayload {
  mediaUrl: string;
  mediaType?: 'image' | 'video';
  soundUrl?: string | null;
  durationMs?: number;
  triggeredBy?: string | null;
  jumpscareId?: string;
  name?: string;
}

interface QueueItem {
  id: string;
  payload: JumpscarePayload;
}

interface Fields {
  /** 0–1. Defaults to 1.0. */
  volume?: number;
  /** Show the "💀 triggeredBy · name" badge at the bottom. Default on. */
  showCreditBadge?: boolean;
}

interface Props {
  event: JumpscarePayload | null;
  fields: Fields;
}

const APEX_HOST = 'https://hypevault.io';

function resolveUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (/^https?:/i.test(url) || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${APEX_HOST}${url}`;
  return url;
}

export function JumpscareWidget({ event, fields }: Props) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [current, setCurrent] = useState<QueueItem | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const dismissTimer = useRef<number | null>(null);
  // Track the last event reference so we only enqueue once per socket frame.
  const lastEventRef = useRef<JumpscarePayload | null>(null);

  const volume = clamp01(fields.volume ?? 1);
  const showCreditBadge = fields.showCreditBadge ?? true;

  // Enqueue when a new event arrives. Parent passes the most recent event,
  // so dedupe by reference — a re-render with the same event object is
  // not a new jumpscare.
  useEffect(() => {
    if (!event?.mediaUrl) return;
    if (event === lastEventRef.current) return;
    lastEventRef.current = event;
    const id = `js-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setQueue((q) => [...q, { id, payload: event }]);
  }, [event]);

  // Pump the queue when current finishes.
  useEffect(() => {
    if (current || queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    setCurrent(next!);
  }, [queue, current]);

  // When the current jumpscare changes, kick off playback + arm the
  // dismiss timer. Hard cap of durationMs + 1s so a stuck media file
  // can never freeze the overlay.
  useEffect(() => {
    if (!current) return;
    const dur = Math.max(800, current.payload.durationMs ?? 3000);
    const hardCap = dur + 1000;
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.volume = volume;
      void videoRef.current.play().catch(() => undefined);
    }
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = volume;
      void audioRef.current.play().catch(() => undefined);
    }
    if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    dismissTimer.current = window.setTimeout(() => {
      setCurrent(null);
      dismissTimer.current = null;
    }, hardCap);
    return () => {
      if (dismissTimer.current) {
        window.clearTimeout(dismissTimer.current);
        dismissTimer.current = null;
      }
    };
  }, [current, volume]);

  if (!current) return null;

  const p = current.payload;
  const mediaUrl = resolveUrl(p.mediaUrl);
  const soundUrl = resolveUrl(p.soundUrl ?? '');
  const mediaType = p.mediaType ?? 'image';

  return (
    <div
      data-jumpscare={current.id}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      {mediaType === 'video' ? (
        <video
          ref={videoRef}
          src={mediaUrl}
          autoPlay
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <img
          src={mediaUrl}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      )}
      {soundUrl && <audio ref={audioRef} src={soundUrl} autoPlay />}
      {showCreditBadge && p.triggeredBy && (
        <div
          style={{
            position: 'absolute',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '6px 16px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontFamily:
              '"Poppins","Rajdhani",system-ui,-apple-system,BlinkMacSystemFont,sans-serif',
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0.5,
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.2)',
          }}
        >
          💀 {p.triggeredBy}
          {p.name ? ` · ${p.name}` : ''}
        </div>
      )}
    </div>
  );
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 1;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
